/**
 * Live matchup math: what the scoreboard will say when the last game ends,
 * how likely you are to win it, and which players are beating their number.
 *
 * Everything here is pure. The fetch layer (`matchupData.js`) hands it Sleeper
 * matchups, the season schedule and an ESPN weekly projection per player; this
 * module turns those into the four numbers a live board has to answer for:
 *
 *   1. Projected final — live points plus whatever is still to be played.
 *   2. Win probability — that margin against the spread of what is left.
 *   3. Player form     — trailing weeks against the same projection.
 *   4. Team streak     — the record Sleeper keeps, read as a run.
 */

/**
 * Weekly standard deviation of fantasy points by position, used as the spread
 * of an unplayed game. Rounded from the 2020-2024 weekly distributions; the
 * win probability only needs the shape, not three decimal places.
 */
const POSITION_SIGMA = {
  QB: 7.5,
  RB: 6.5,
  WR: 7,
  TE: 5,
  K: 3.5,
  DEF: 6,
};
const DEFAULT_SIGMA = 6;

/** Fraction of a game elapsed when the feed says "in progress" but not how far. */
const IN_PROGRESS_FALLBACK = 0.5;

const QUARTER_MINUTES = 15;
const REGULATION_MINUTES = 60;

const clamp01 = (value) => Math.min(1, Math.max(0, value));

const toNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

/** "7:32" → 7.53 minutes. Returns null for anything that is not a clock. */
const parseClockMinutes = (clock) => {
  if (typeof clock === 'number') return Number.isFinite(clock) ? clock : null;
  const match = /^(\d{1,2}):(\d{2})$/.exec(String(clock || '').trim());
  if (!match) return null;
  return Number(match[1]) + Number(match[2]) / 60;
};

/**
 * How much of a player's game is already in the books, 0 → 1.
 *
 * A finished game is 1 and an unkicked one is 0. In between we use the clock
 * when the schedule feed carries one and fall back to half a game when it does
 * not, which is the honest answer to "somewhere in progress".
 */
export const gameProgress = (starter) => {
  if (!starter) return 0;
  if (starter.hasPlayed) return 1;
  if (starter.onBye) return 1;
  if (!starter.isLocked) return 0;

  const quarter = toNumber(starter.quarter);
  const remaining = parseClockMinutes(starter.timeRemaining);
  if (quarter != null && quarter >= 1 && quarter <= 4 && remaining != null) {
    const elapsed = (quarter - 1) * QUARTER_MINUTES + (QUARTER_MINUTES - remaining);
    return clamp01(elapsed / REGULATION_MINUTES);
  }
  if (quarter != null && quarter >= 1) {
    return clamp01(Math.min(quarter, 4) / 4 - 0.125);
  }
  return IN_PROGRESS_FALLBACK;
};

/**
 * A starter's expected final score: what he has banked plus the share of his
 * projection the clock has not spent yet.
 */
export const projectStarter = (starter) => {
  if (!starter) {
    return { live: 0, projection: 0, expected: 0, remaining: 0, progress: 1, sigma: 0 };
  }

  const progress = gameProgress(starter);
  const projection = Math.max(0, toNumber(starter.projection) ?? 0);
  const live = toNumber(starter.livePoints);
  const banked = live != null ? live : progress > 0 ? toNumber(starter.points) ?? 0 : 0;
  const remaining = projection * (1 - progress);
  const sigma = (POSITION_SIGMA[starter.position] ?? DEFAULT_SIGMA) * (1 - progress);

  return {
    live: banked,
    projection,
    expected: Math.round((banked + remaining) * 100) / 100,
    remaining: Math.round(remaining * 100) / 100,
    progress,
    sigma,
  };
};

const round1 = (value) => Math.round(value * 10) / 10;

/**
 * Roll a team's starters into a projected final, plus the pieces the board
 * shows beside it: points banked, points still to come, and how many players
 * have not finished.
 */
export const projectTeam = (starters = [], fallbackPoints = 0) => {
  const rows = starters.map((starter) => ({ starter, math: projectStarter(starter) }));

  const live = rows.reduce((sum, row) => sum + row.math.live, 0);
  const remaining = rows.reduce((sum, row) => sum + row.math.remaining, 0);
  const variance = rows.reduce((sum, row) => sum + row.math.sigma ** 2, 0);

  const yetToPlay = rows.filter((row) => row.math.progress === 0 && !row.starter.onBye).length;
  const inProgress = rows.filter(
    (row) => row.math.progress > 0 && row.math.progress < 1
  ).length;
  const done = rows.length - yetToPlay - inProgress;

  // Sleeper's own team total is authoritative for banked points once scoring
  // is live; our per-player sum can drift on stat corrections.
  const banked = fallbackPoints > 0 ? fallbackPoints : live;

  return {
    live: round1(banked),
    remaining: round1(remaining),
    projected: round1(banked + remaining),
    variance,
    sigma: Math.sqrt(variance),
    yetToPlay,
    inProgress,
    done,
    starters: rows.length,
  };
};

/** Abramowitz & Stegun 7.1.26 — enough precision for a win-probability chip. */
const erf = (x) => {
  const sign = x < 0 ? -1 : 1;
  const absX = Math.abs(x);
  const t = 1 / (1 + 0.3275911 * absX);
  const y =
    1 -
    ((((1.061405429 * t - 1.453152027) * t + 1.421413741) * t - 0.284496736) * t +
      0.254829592) *
      t *
      Math.exp(-absX * absX);
  return sign * y;
};

export const normalCdf = (z) => 0.5 * (1 + erf(z / Math.SQRT2));

/**
 * Win probability from the projected margin and the spread of what is left.
 *
 * With every game final there is no spread left, so the margin decides it
 * outright. Otherwise the margin is read against the combined standard
 * deviation of both teams' unplayed minutes.
 */
export const winProbability = (mine, opponent) => {
  const margin = (mine?.projected ?? 0) - (opponent?.projected ?? 0);
  const variance = (mine?.variance ?? 0) + (opponent?.variance ?? 0);

  if (variance <= 0.0001) {
    if (Math.abs(margin) < 0.005) return 0.5;
    return margin > 0 ? 1 : 0;
  }

  return clamp01(normalCdf(margin / Math.sqrt(variance)));
};

/**
 * Weekly points a startable player at each position is worth when nothing
 * better is known. ESPN's feed prices 135 skill players and no kickers or
 * defenses at all, so without this every K and DST on the board projects zero
 * and the projected final is short by a dozen points a side.
 *
 * These are roughly the 2024 weekly medians among players actually started.
 */
const POSITION_BASELINE = {
  QB: 14,
  RB: 8,
  WR: 8,
  TE: 6,
  K: 8,
  DEF: 7,
};

/**
 * A player's weekly projection, best source first: ESPN's own number, then
 * what he has actually averaged in this league, then a positional baseline.
 * The source travels with the number so the board can mark an estimate.
 */
export const resolveProjection = ({ espn, formAvg, position }) => {
  const espnValue = toNumber(espn);
  if (espnValue != null && espnValue > 0) {
    return { projection: espnValue, source: 'espn' };
  }
  const form = toNumber(formAvg);
  if (form != null && form > 0) {
    return { projection: Math.round(form * 10) / 10, source: 'form' };
  }
  const baseline = POSITION_BASELINE[position];
  if (baseline) {
    return { projection: baseline, source: 'baseline' };
  }
  return { projection: 0, source: 'none' };
};

/**
 * Win probability as the board states it. While a single snap is unplayed the
 * answer is never 0 or 100 — rounding to certainty is a claim the model has
 * not earned — so the displayed number is held inside 1–99 until every game
 * is final.
 */
export const displayWinPct = (probability, gamesRemaining) => {
  const pct = Math.round((probability ?? 0) * 100);
  if (!gamesRemaining) return pct;
  return Math.min(99, Math.max(1, pct));
};

/**
 * Current run from Sleeper's record string ("WLWWL", most recent last).
 * Falls back to the win/loss counters when no string is published.
 */
export const parseStreak = (record, settings = {}) => {
  const chars = String(record || '')
    .toUpperCase()
    .replace(/[^WLT]/g, '');

  if (!chars.length) {
    const wins = Number(settings.wins || 0);
    const losses = Number(settings.losses || 0);
    if (!wins && !losses) return null;
    return null;
  }

  const latest = chars[chars.length - 1];
  let length = 0;
  for (let i = chars.length - 1; i >= 0 && chars[i] === latest; i -= 1) {
    length += 1;
  }

  return {
    type: latest,
    length,
    label: `${latest}${length}`,
    form: chars.slice(-5).split(''),
  };
};

/** Record + streak + scoring, read off a Sleeper roster row. */
export const summarizeTeamRecord = (roster, allRosters = []) => {
  const settings = roster?.settings || {};
  const wins = Number(settings.wins || 0);
  const losses = Number(settings.losses || 0);
  const ties = Number(settings.ties || 0);
  const games = wins + losses + ties;

  const pointsFor = Number(settings.fpts || 0) + Number(settings.fpts_decimal || 0) / 100;
  const pointsAgainst =
    Number(settings.fpts_against || 0) + Number(settings.fpts_against_decimal || 0) / 100;

  const allPoints = allRosters
    .map((r) => Number(r?.settings?.fpts || 0) + Number(r?.settings?.fpts_decimal || 0) / 100)
    .filter((value) => value > 0)
    .sort((a, b) => b - a);
  const scoringRank = allPoints.length ? allPoints.indexOf(pointsFor) + 1 : null;
  const leagueAvg = allPoints.length
    ? allPoints.reduce((sum, value) => sum + value, 0) / allPoints.length
    : 0;

  return {
    wins,
    losses,
    ties,
    games,
    record: ties ? `${wins}-${losses}-${ties}` : `${wins}-${losses}`,
    streak: parseStreak(roster?.metadata?.record, settings),
    pointsFor: round1(pointsFor),
    pointsAgainst: round1(pointsAgainst),
    pointsPerGame: games ? round1(pointsFor / games) : null,
    scoringRank: scoringRank && scoringRank > 0 ? scoringRank : null,
    teams: allPoints.length || allRosters.length || null,
    vsLeagueAvg: leagueAvg ? round1(pointsFor - leagueAvg) : null,
  };
};

/** Trailing weeks worth keeping in a form read. */
export const FORM_WINDOW = 5;

/**
 * A player's trailing weeks against the same projection the board is using.
 *
 * `delta` is the number the board leads with: points per game above or below
 * what he was projected for. Everything else — the run of weeks over the
 * number, the last-three trend — is there to say whether that gap is a trend
 * or one loud Sunday.
 */
export const playerForm = (history = [], projection = null) => {
  const games = history
    .filter((row) => row && row.played !== false && toNumber(row.points) != null)
    .sort((a, b) => a.week - b.week)
    .slice(-FORM_WINDOW);

  if (!games.length) return null;

  const points = games.map((row) => Number(row.points));
  const total = points.reduce((sum, value) => sum + value, 0);
  const avg = total / points.length;
  const last = points[points.length - 1];

  const baseline = toNumber(projection);
  const hasBaseline = baseline != null && baseline > 0;
  const delta = hasBaseline ? avg - baseline : null;
  const overWeeks = hasBaseline ? points.filter((value) => value > baseline).length : 0;

  const recent = points.slice(-3);
  const earlier = points.slice(0, -3);
  const recentAvg = recent.reduce((sum, value) => sum + value, 0) / recent.length;
  const earlierAvg = earlier.length
    ? earlier.reduce((sum, value) => sum + value, 0) / earlier.length
    : null;

  let trend = 'flat';
  if (earlierAvg != null) {
    if (recentAvg - earlierAvg >= 2) trend = 'up';
    else if (earlierAvg - recentAvg >= 2) trend = 'down';
  }

  // Consecutive most-recent weeks on the same side of the projection.
  let run = 0;
  if (hasBaseline) {
    const over = last > baseline;
    for (let i = points.length - 1; i >= 0; i -= 1) {
      if (points[i] > baseline === over) run += 1;
      else break;
    }
    run = over ? run : -run;
  }

  return {
    weeks: games.map((row) => ({ week: row.week, points: round1(Number(row.points)) })),
    games: points.length,
    avg: round1(avg),
    last: round1(last),
    high: round1(Math.max(...points)),
    low: round1(Math.min(...points)),
    projection: hasBaseline ? round1(baseline) : null,
    delta: delta == null ? null : round1(delta),
    deltaPct: hasBaseline && baseline ? Math.round((avg / baseline - 1) * 100) : null,
    overWeeks,
    overRate: hasBaseline ? Math.round((overWeeks / points.length) * 100) : null,
    run,
    trend,
  };
};

const SLOT_LABELS = {
  SUPER_FLEX: 'SFLX',
  REC_FLEX: 'RFLX',
  FLEX: 'FLEX',
  DEF: 'DST',
};

export const slotLabel = (slot) => SLOT_LABELS[slot] || slot;

const STARTER_SLOTS = new Set([
  'QB',
  'RB',
  'WR',
  'TE',
  'FLEX',
  'SUPER_FLEX',
  'REC_FLEX',
  'K',
  'DEF',
  'IDP_FLEX',
  'DL',
  'LB',
  'DB',
]);

/**
 * Pair the two lineups slot by slot. Sleeper orders `starters` to match
 * `roster_positions`, so index i is the same slot on both sides.
 */
export const pairStarters = (rosterPositions = [], mine = [], opponent = []) => {
  const slots = rosterPositions.filter((slot) => STARTER_SLOTS.has(slot));
  const depth = Math.max(mine.length, opponent.length, slots.length);

  const rows = [];
  for (let i = 0; i < depth; i += 1) {
    const mineStarter = mine[i] || null;
    const oppStarter = opponent[i] || null;
    if (!mineStarter && !oppStarter) continue;

    const mineMath = mineStarter ? projectStarter(mineStarter) : null;
    const oppMath = oppStarter ? projectStarter(oppStarter) : null;

    rows.push({
      key: `${i}-${mineStarter?.id || 'none'}-${oppStarter?.id || 'none'}`,
      slot: slots[i] || mineStarter?.position || oppStarter?.position || 'FLEX',
      mine: mineStarter,
      opponent: oppStarter,
      mineMath,
      oppMath,
      liveEdge: round1((mineMath?.live ?? 0) - (oppMath?.live ?? 0)),
      projectedEdge: round1((mineMath?.expected ?? 0) - (oppMath?.expected ?? 0)),
    });
  }

  return rows;
};

/**
 * The single line the card leads with: who is ahead, by how much, and whether
 * the games left still decide it.
 */
export const matchupHeadline = (mine, opponent, probability) => {
  const margin = round1(mine.projected - opponent.projected);
  const liveMargin = round1(mine.live - opponent.live);
  const left = mine.yetToPlay + mine.inProgress + opponent.yetToPlay + opponent.inProgress;

  if (!left) {
    if (margin > 0) return { tone: 'win', text: `Won by ${Math.abs(margin)}` };
    if (margin < 0) return { tone: 'loss', text: `Lost by ${Math.abs(margin)}` };
    return { tone: 'even', text: 'Tied' };
  }

  const pct = displayWinPct(probability, left);
  const side = margin >= 0 ? 'up' : 'down';
  const verb = margin >= 0 ? 'ahead' : 'behind';
  return {
    tone: pct >= 65 ? 'win' : pct <= 35 ? 'loss' : 'even',
    text: `${Math.abs(margin)} ${verb} on projection · ${pct}% to win`,
    side,
    liveMargin,
    playersLeft: left,
  };
};

/** Starters whose week is not yet decided, biggest projected swing first. */
export const swingStarters = (rows = [], limit = 3) =>
  rows
    .flatMap((row) => [
      row.mine ? { ...row.mine, math: row.mineMath, side: 'mine', slot: row.slot } : null,
      row.opponent
        ? { ...row.opponent, math: row.oppMath, side: 'opponent', slot: row.slot }
        : null,
    ])
    .filter((entry) => entry && entry.math && entry.math.remaining > 0)
    .sort((a, b) => b.math.remaining - a.math.remaining)
    .slice(0, limit);
